<template>
  <div class="p-6 max-w-6xl mx-auto">
    <h2 class="text-2xl font-bold mb-6">{{ isEditing ? 'Редактировать заказ' : 'Добавить заказ' }}</h2>
    
    <form @submit.prevent="submitForm" class="space-y-6">
      <!-- Основная информация о заказе - две колонки -->
      <div class="bg-white p-6 rounded-lg border border-gray-200">
        <h3 class="text-lg font-semibold mb-4">Основная информация</h3>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Номер заказа -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Номер заказа</label>
            <input
              type="text"
              v-model="form.number"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите номер заказа"
              required
            />
          </div>
          
          <!-- Статус -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Статус</label>
            <v-select
              v-model="form.status"
              :reduce="status => status.key"
              :options="statuses"
              label="name"
              placeholder="Выберите статус"
              class="w-full"
            >
              <template #no-options="{ search, searching, loading }">
                Статусы не найдены.
              </template>
            </v-select>
          </div>
          
          <!-- Покупатель -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Покупатель</label>
            <v-select
              v-model="form.company_id"
              :reduce="company => company"
              :options="companies"
              :filterable="false"
              label="name"
              placeholder="Начните вводить название или ИНН..."
              :loading="loadingCompanies"
              @search="onCompanySearch"
              class="w-full"
            >
              <template #search="{attributes, events}">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                />
              </template>
              <template #no-options="{ search, searching, loading }">
                <span v-if="loadingCompanies">Загружается...</span>
                <span v-else>Покупатели не найдены</span>
              </template>
            </v-select>
          </div>
          
          <!-- Плановая дата отгрузки -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Плановая дата отгрузки</label>
            <input
              type="date"
              v-model="form.plan_date_shipment"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <!-- Продукция -->
      <div class="bg-white p-6 rounded-lg border border-gray-200">
        <h3 class="text-lg font-semibold mb-4">Продукция в заказе</h3>
        
        <!-- Форма добавления продукта -->
        <div class="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 class="text-md font-medium mb-3">Добавить продукт</h4>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Продукт</label>
              <v-select
                v-model="selectedProduct"
                :options="availableProducts"
                :filterable="false"
                label="name"
                placeholder="Начните вводить название или артикул..."
                :loading="loadingProducts"
                @search="onProductSearch"
                class="w-full"
              >
                <template #search="{attributes, events}">
                  <input
                    class="vs__search"
                    v-bind="attributes"
                    v-on="events"
                  />
                </template>
                <template #option="option">
                  <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
                </template>
                <template #selected-option="option">
                  <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
                </template>
                <template #no-options="{ search }">
                  <span v-if="loadingProducts">Загружается...</span>
                  <span v-else>Продукты не найдены</span>
                </template>
              </v-select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Количество</label>
              <input
                v-model="productCount"
                type="number"
                min="1"
                placeholder="Кол-во"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div class="flex items-end">
                             <button
                 v-can-write="moduleId"
                 @click="addProduct"
                 type="button"
                 class="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                 :disabled="!selectedProduct || !productCount || productCount <= 0"
               >
                 Добавить продукт
               </button>
            </div>
          </div>
        </div>

        <!-- Таблица продуктов -->
        <div v-if="selectedProducts.length > 0">
          <div class="overflow-x-auto">
            <table class="w-full border-collapse border border-gray-300">
              <thead>
                <tr class="bg-gray-100">
                  <th class="border border-gray-300 px-4 py-2 text-left">Продукт</th>
                  <th class="border border-gray-300 px-4 py-2 text-left">Количество</th>
                  <th class="border border-gray-300 px-4 py-2 text-center">Действия</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, index) in selectedProducts" :key="index" class="hover:bg-gray-50">
                  <td class="border border-gray-300 px-4 py-3">
                    <span :class="{ 'line-through text-gray-500': product.deleted_at }">{{ product.name }}</span>
                    <span v-if="product.deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                  </td>
                  <td class="border border-gray-300 px-4 py-3">{{ product.count }}</td>
                  <td class="border border-gray-300 px-4 py-3 text-center">
                                         <button
                       v-can-write="moduleId"
                       @click="removeProduct(product.id)"
                       class="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 cursor-pointer"
                     >
                       Удалить
                     </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="text-center py-8 text-gray-500">
          Продукты не добавлены
        </div>
      </div>

      <!-- Кнопки -->
      <div class="flex justify-end space-x-3">
        <button
          type="button"
          @click="goBack"
          class="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 cursor-pointer"
        >
          Отмена
        </button>
        <button
          v-can-write="moduleId"
          type="submit"
          class="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!form.number || !form.status || selectedProducts.length === 0"
        >
          {{ isEditing ? 'Сохранить' : 'Создать заказ' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import orderStatusesMixin from '@/mixins/orderStatusesMixin';
import vSelect from 'vue-select';

export default {
  components: {
    vSelect
  },
  mixins: [orderStatusesMixin],
  props: {
    moduleId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      form: {
        id: null,
        number: '',
        company_id: '',
        status: '',
        plan_date_shipment: '',
      },
      isEditing: false,
      companies: [],
      availableProducts: [],
      selectedProducts: [],
      selectedProduct: null,
      productCount: null,
      loadingProducts: false,
      productSearchTimer: null,
      loadingCompanies: false,
      companySearchTimer: null,
    };
  },
  created() {
    this.onCompanySearch('');
    this.onProductSearch('');
    if (this.$route.params.id) {
      this.isEditing = true;
      this.fetchOrder(this.$route.params.id);
    }
  },
  methods: {
    onCompanySearch(search) {
      clearTimeout(this.companySearchTimer);
      this.companySearchTimer = setTimeout(() => {
        this.loadingCompanies = true;
        axios
          .post('/api/company/getCompanies', {
            page: 1,
            per_page: 50,
            search: search?.trim() || ''
          })
          .then((response) => {
            const d = response.data.data;
            this.companies = d.data || [];
          })
          .catch((error) => {
            console.error('Ошибка при загрузке компаний:', error);
            this.companies = [];
          })
          .finally(() => {
            this.loadingCompanies = false;
          });
      }, 350);
    },
    onProductSearch(search) {
      clearTimeout(this.productSearchTimer);
      this.productSearchTimer = setTimeout(() => {
        this.loadingProducts = true;
        axios
          .post('/api/product/getProducts', { page: 1, per_page: 50, search: search?.trim() || '' })
          .then((response) => {
            const d = response.data?.data;
            this.availableProducts = d?.data || [];
          })
          .catch((error) => {
            console.error('Ошибка при загрузке продуктов:', error);
            this.availableProducts = [];
          })
          .finally(() => {
            this.loadingProducts = false;
          });
      }, 350);
    },
    fetchOrder(id) {
      axios
        .post('/api/order/getOrder', {id})
        .then((response) => {
          const order = response.data.data;
          this.form = {
            ...order,
            company_id: order.company || null,
          };
          this.selectedProducts = order.products.map((p) => ({
            id: p.product_id,
            name: p.name,
            count: parseFloat(p.count) || 0,
            deleted_at: p.deleted_at,
          }));
        })
        .catch((error) => {
          console.error('Ошибка при загрузке заказа:', error);
        });
    },
    submitForm() {
      if (!this.form.number || !this.form.status) {
        alert('Заполните все обязательные поля');
        return;
      }
      if (!this.selectedProducts.length) {
        alert('Добавьте хотя бы один продукт');
        return;
      }
      
      const formData = {
        ...this.form,
        company_id: this.form.company_id?.id || null,
      };
      if (this.isEditing) {
        formData.id = this.form.id;
      }
      formData.products = this.selectedProducts.map((p) => ({
        product_id: p.id,
        count: p.count,
      }));
      
      axios
        .post('/api/order/createOrUpdateOrder', formData)
        .then(() => {
          this.goBack();
        })
        .catch((error) => {
          console.error('Ошибка при отправке данных:', error);
          alert('Произошла ошибка при сохранении заказа');
        });
    },
    goBack() {
      const moduleId = this.moduleId || this.$route.query.moduleId;
      if (moduleId) {
        this.$router.push({
          name: 'Orders',
          params: { moduleId },
          query: this.$route.query
        });
      } else {
        console.error('Module ID is missing');
      }
    },
    addProduct(event) {
      event.preventDefault();
      const product = this.selectedProduct;
      if (
        product &&
        !this.selectedProducts.find((sp) => sp.id === product.id) &&
        this.productCount > 0
      ) {
        this.selectedProducts.push({
          id: product.id,
          name: product.name,
          count: this.productCount,
        });
        this.selectedProduct = null;
        this.productCount = null;
      }
    },
    removeProduct(productId) {
      const index = this.selectedProducts.findIndex(p => p.id === productId);
      if (index !== -1) {
        this.selectedProducts.splice(index, 1);
      }
    }
  },
};
</script>
